import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authenticatedFetch } from '../services/authService';

const BACKEND = process.env.REACT_APP_BACKEND_API_URL || '/api';

const scan = () =>
    authenticatedFetch(`${BACKEND}/provision/scan`, { method: 'POST' })
        .then(r => r.json());

const execute = (users) =>
    authenticatedFetch(`${BACKEND}/provision/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users }),
    }).then(r => r.json());

const ProvisionModal = ({ onClose, isAdmin }) => {
    const { t } = useTranslation();

    // stage: 'scanning' | 'review' | 'executing' | 'results' | 'error'
    const [stage, setStage] = useState('scanning');
    const [scanData, setScanData] = useState(null);
    const [selected, setSelected] = useState({});
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    const doScan = useCallback(async () => {
        setStage('scanning');
        setError(null);
        try {
            const data = await scan();
            if (!data.success) throw new Error(data.error || 'Scan failed');
            setScanData(data);
            // Pre-select all assignable users
            const init = {};
            data.unassigned.forEach(u => {
                if (u.targetGroupId) init[u.id] = true;
            });
            setSelected(init);
            setStage('review');
        } catch (err) {
            setError(err.message);
            setStage('error');
        }
    }, []);

    useEffect(() => { doScan(); }, [doScan]);

    const toggleAll = (val) => {
        if (!scanData) return;
        const next = {};
        scanData.unassigned.forEach(u => {
            if (u.targetGroupId) next[u.id] = val;
        });
        setSelected(next);
    };

    const toggleOne = (id) => {
        setSelected(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const selectedUsers = scanData
        ? scanData.unassigned.filter(u => u.targetGroupId && selected[u.id])
        : [];

    const doExecute = async () => {
        if (selectedUsers.length === 0) return;
        setStage('executing');
        try {
            const data = await execute(selectedUsers);
            setResults(data);
            setStage('results');
        } catch (err) {
            setError(err.message);
            setStage('error');
        }
    };

    const renderContent = () => {
        if (stage === 'scanning') {
            return (
                <div className="provision-status">
                    <div className="provision-spinner" />
                    <div className="provision-status-text">{t('provision.scanning')}</div>
                </div>
            );
        }

        if (stage === 'executing') {
            return (
                <div className="provision-status">
                    <div className="provision-spinner" />
                    <div className="provision-status-text">{t('provision.executing')}</div>
                </div>
            );
        }

        if (stage === 'error') {
            return (
                <div className="provision-status">
                    <div className="provision-error-msg">{error}</div>
                    <button className="btn-primary" onClick={doScan}>{t('provision.retry')}</button>
                </div>
            );
        }

        if (stage === 'results') {
            return (
                <div className="provision-results">
                    <div className="provision-results-summary">
                        <span className="provision-success-count">{results.succeeded} {t('provision.succeeded')}</span>
                        {results.failed > 0 && (
                            <span className="provision-fail-count">{results.failed} {t('provision.failed')}</span>
                        )}
                    </div>
                    <div className="provision-results-list">
                        {results.results.map((r, i) => (
                            <div key={i} className={`provision-result-row ${r.success ? 'ok' : 'fail'}`}>
                                <span className="provision-result-email">{r.email}</span>
                                {r.success
                                    ? <span className="provision-result-group">{r.group}</span>
                                    : <span className="provision-result-err">{r.error}</span>
                                }
                            </div>
                        ))}
                    </div>
                    <div className="provision-footer">
                        <button className="btn-secondary" onClick={doScan}>{t('provision.scanAgain')}</button>
                        <button className="btn-primary" onClick={onClose}>{t('common.close')}</button>
                    </div>
                </div>
            );
        }

        // stage === 'review'
        const assignable = scanData.unassigned.filter(u => u.targetGroupId);
        const needsReview = scanData.unassigned.filter(u => !u.targetGroupId);
        const allSelected = assignable.length > 0 && assignable.every(u => selected[u.id]);

        return (
            <div className="provision-review">
                <div className="provision-summary-row">
                    <span className="provision-count-badge">{scanData.total} {t('provision.unassigned')}</span>
                    <span className="provision-count-badge ok">{assignable.length} {t('provision.readyToAssign')}</span>
                    {needsReview.length > 0 && (
                        <span className="provision-count-badge warn">{needsReview.length} {t('provision.needsReview')}</span>
                    )}
                </div>

                {assignable.length > 0 && (
                    <>
                        <div className="provision-table-header">
                            <label className="provision-check-label">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={e => toggleAll(e.target.checked)}
                                />
                                {t('provision.selectAll')} ({assignable.length})
                            </label>
                            <span className="provision-col-head">{t('provision.colName')}</span>
                            <span className="provision-col-head">{t('provision.colOffice')}</span>
                            <span className="provision-col-head">{t('provision.colGroup')}</span>
                        </div>

                        <div className="provision-table-body">
                            {assignable.map(u => (
                                <div key={u.id} className="provision-table-row">
                                    <label className="provision-check-label">
                                        <input
                                            type="checkbox"
                                            checked={!!selected[u.id]}
                                            onChange={() => toggleOne(u.id)}
                                        />
                                    </label>
                                    <span className="provision-cell-name">{u.name}</span>
                                    <span className="provision-cell-office">{u.officeLocation || '—'}</span>
                                    <span className="provision-cell-group">{u.targetGroup}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {needsReview.length > 0 && (
                    <div className="provision-needs-review">
                        <div className="provision-needs-review-title">{t('provision.cannotAutoAssign')}</div>
                        {needsReview.map(u => (
                            <div key={u.id} className="provision-table-row muted">
                                <span className="provision-cell-name">{u.name}</span>
                                <span className="provision-cell-office">{u.officeLocation || '—'}</span>
                                <span className="provision-cell-reason">{u.reason}</span>
                            </div>
                        ))}
                    </div>
                )}

                {assignable.length === 0 && needsReview.length === 0 && (
                    <div className="provision-all-assigned">{t('provision.allAssigned')}</div>
                )}

                <div className="provision-footer">
                    <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
                    <button
                        className="btn-primary"
                        onClick={doExecute}
                        disabled={selectedUsers.length === 0}
                    >
                        {t('provision.assignSelected', { count: selectedUsers.length })}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal provision-modal">
                <div className="modal-header">
                    <h2 className="modal-title">{t('provision.title')}</h2>
                    <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="modal-body provision-modal-body">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ProvisionModal;

